import { getApp } from 'firebase/app'
import { getAuth } from 'firebase/auth'
import { createContext, PropsWithChildren, useContext, useEffect, useMemo, useState } from 'react'
import { useToggle } from 'siteline-common-web/hooks/useToggle'
import { identifyUser, removeUserIdentification } from 'siteline-common-web/utils/User'
import { Loader } from './Loader'

type AuthContextValue = {
  userId: string | null
  email: string | null
}

const AuthContext = createContext<AuthContextValue>({
  userId: null,
  email: null,
})

export function useAdminAuthContext() {
  return useContext(AuthContext)
}

/**
 * Tracks the signed-in admin and renders a loader until Firebase has resolved the auth state, so
 * the router never mounts while we don't yet know whether the user is authenticated.
 */
export function AdminAuthProvider({ children }: PropsWithChildren) {
  const [userId, setUserId] = useState<string | null>(null)
  const [email, setEmail] = useState<string | null>(null)
  const [isLoading, , handleNotLoading] = useToggle(true)

  useEffect(() => {
    const auth = getAuth(getApp())
    const unsubscribe = auth.onAuthStateChanged((user) => {
      if (user) {
        setUserId(user.uid)
        setEmail(user.email)
        void identifyUser({
          id: user.uid,
          email: user.email ?? undefined,
        })
      } else {
        setUserId(null)
        setEmail(null)
        void removeUserIdentification()
      }
      handleNotLoading()
    })
    return unsubscribe
  }, [handleNotLoading])

  const contextValue = useMemo(() => ({ userId, email }), [userId, email])

  if (isLoading) {
    return <Loader />
  }

  return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
}
